/**
 * HeaderActions — the page header's right-hand cluster of PAGE actions,
 * anchored top-right at every width.
 *
 * Scope: page verbs only (Back, Cancel Event, PDF links…). Global
 * destinations and Logout used to be injected here, which buried the app's
 * navigation behind a kebab and sat it beside destructive per-page actions —
 * they now live in the persistent `AppNav` bar (see AppNav.tsx).
 *
 * Layout: inline row from `sm` up, where there is width for it; below `sm` the
 * actions collapse behind a kebab that stacks them full-width. The kebab was
 * previously used at every width — a mobile fix that had eaten the desktop
 * header. An optional `badge` (e.g. the event phase) stays visible at all
 * widths.
 *
 * A single action never collapses: a kebab holding one button is two taps and
 * a guess where there was room for the button itself (equipment detail's lone
 * "Back"). The kebab earns its place only once it is hiding a list.
 *
 * DESIGN-SYSTEM.md: actions are the shared `Button` component (no hover visual change);
 * the dropdown is just those buttons stacked — no bespoke menu-item styling.
 */
import { useRef, useState, type ReactNode } from "react";
import { cx } from "@shared/cx";
import { Button } from "./Button";
import { MoreVerticalIcon, XIcon } from "./icons";
import { useDismissable } from "../../lib/use-dismissable";
import styles from "./HeaderActions.module.css";

export type HeaderAction =
  | { kind: "link"; label: string; to: string }
  | {
      kind: "external-link";
      label: string;
      href: string;
      target?: string;
      rel?: string;
    }
  | {
      kind: "button";
      label: string;
      onClick: () => void;
      variant?: "secondary" | "danger";
      disabled?: boolean;
    };

function renderAction(
  action: HeaderAction,
  fullWidth: boolean,
  onActivate?: () => void,
): ReactNode {
  const cls = cx(styles.action, fullWidth && styles.actionFull);
  if (action.kind === "link") {
    // Route nav unmounts this header, so the menu closes on its own.
    return (
      <Button
        key={action.label}
        to={action.to}
        variant="secondary"
        size="sm"
        className={cls}
      >
        {action.label}
      </Button>
    );
  }
  if (action.kind === "external-link") {
    return (
      <Button
        key={action.label}
        href={action.href}
        target={action.target}
        rel={action.rel}
        variant="secondary"
        size="sm"
        className={cls}
      >
        {action.label}
      </Button>
    );
  }
  return (
    <Button
      key={action.label}
      variant={action.variant ?? "secondary"}
      size="sm"
      disabled={action.disabled}
      onClick={() => {
        action.onClick();
        onActivate?.();
      }}
      className={cls}
    >
      {action.label}
    </Button>
  );
}

export function HeaderActions({
  actions,
  badge,
}: {
  actions: HeaderAction[];
  badge?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // One action is its own affordance at every width; only a list collapses.
  const collapses = actions.length > 1;

  useDismissable({
    open,
    onDismiss: () => setOpen(false),
    pointer: [menuRef],
  });

  return (
    <div className={styles.cluster}>
      {badge}

      {actions.length > 0 && (
        <>
          {/* The actions themselves, in a row: from `sm` when they collapse,
              at every width when there is only one. */}
          <div
            className={cx(
              styles.actionRow,
              collapses && styles.actionRowCollapsing,
            )}
          >
            {actions.map((a) => renderAction(a, false))}
          </div>

          {/* Mobile: kebab → the same actions stacked full-width. */}
          {collapses && (
            <div ref={menuRef} className={styles.kebab}>
              <button
                type="button"
                aria-label={open ? "Close menu" : "Open menu"}
                aria-expanded={open}
                aria-haspopup="menu"
                onClick={() => setOpen((o) => !o)}
                className={styles.kebabButton}
              >
                {open ? (
                  <XIcon className={styles.kebabIcon} />
                ) : (
                  <MoreVerticalIcon className={styles.kebabIcon} />
                )}
              </button>
              {open && (
                <div role="menu" className={styles.menuPanel}>
                  {actions.map((a) =>
                    renderAction(a, true, () => setOpen(false)),
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
